import { AutoComplete, Form, Input, Modal, notification, Space } from 'antd';
import CustomButton from 'components/custom-button';
import { UPLOAD_TYPE } from 'constants/common';
import { useCallback, useEffect, useState } from 'react';
import { API } from 'service/api-route';
import AxiosUtils from 'utils/axios-utils';
import styles from '../styles.module.scss';
import CustomizedUpload from 'components/customized-upload';
import { SERVER_FILE_ENDPOINT } from 'constants/envs';
import { REQUIRED } from '../../../constants/rule-form';
import dayjs from 'dayjs';
import DebounceSelect from '../../../components/debounce-select/select';
import * as _ from 'lodash';
// Nhóm DOMESTIC đi qua cổng Studio: lỗi 409/503 có câu tiếng Việt ở `error` (sai Quản lý, trùng tên file…).
import { mapStudioError } from 'pages/studio/studio-error';
import { getManagerCandidates } from 'pages/studio/api';

interface DataPost {
  title: string;
  tiktokGroupName?: string;
  avatar: any;
  description: string;
  ownerId: any;
  assistant: any;
  type?: string;
}

interface Props {
  isOpen: boolean;
  handleCancelCreateEditModal: () => void;
  onChangeStatusUpdateData: () => void;
  valueUserSelected: any;
}

const ModalCreateEdit = ({
  isOpen,
  valueUserSelected,
  handleCancelCreateEditModal,
  onChangeStatusUpdateData,
}: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tiktokNames, setTiktokNames] = useState<string[]>([]);
  useEffect(() => {
    AxiosUtils.get(`${API.revenues}/agency-groups`)
      .then((res: any) => setTiktokNames(res?.data || []))
      .catch(() => setTiktokNames([]));
  }, []);
  const onFinish = async (values: DataPost) => {
    values.avatar = values?.avatar?.fileKey;
    values.ownerId = values?.ownerId?.value;
    values.assistant = _.map(values.assistant, 'value').toString() || '';
    values.type = 'DOMESTIC';
    setLoading(true);
    if (valueUserSelected?.id) {
      try {
        const res: any = await AxiosUtils.put(`${API.createGroup}/${valueUserSelected.id}`, {
          ...values,
        });
        if (res?.status === 200) {
          handleCancelCreateEditModal();
          onChangeStatusUpdateData();
          notification.success({
            message: 'Thành công',
            description: 'Sửa thành công!',
            duration: 4,
          });
        }
      } catch (error) {
        notification.error({
          message: 'Không thành công',
          description: mapStudioError(error),
          duration: 4,
        });
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const res: any = await AxiosUtils.post(`${API.createGroup}`, { ...values });
        if (res?.status === 201) {
          handleCancelCreateEditModal();
          onChangeStatusUpdateData();
          notification.success({
            message: 'Thành công',
            description: 'Tạo thành công!',
            duration: 4,
          });
        }
      } catch (error) {
        notification.error({
          message: 'Không thành công',
          description: mapStudioError(error),
          duration: 4,
        });
      } finally {
        setLoading(false);
      }
    }
  };
  useEffect(() => {
    if (valueUserSelected) {
      // Nhóm chưa có trưởng team: để ô trống (không phải object rỗng) — chọn người ở đây là bổ sung Quản lý.
      const ownerId: any = valueUserSelected?.owner?.id
        ? {
            key: valueUserSelected.owner.id,
            value: valueUserSelected.owner.id,
            label: valueUserSelected.owner.username,
          }
        : undefined;

      const listAssistant =
        (valueUserSelected?.assistant || []).map((item: any) => {
          return {
            key: item.id,
            value: item.id,
            label: item.username,
          };
        }) || [];

      form.setFieldsValue({
        title: valueUserSelected?.title || '',
        tiktokGroupName: valueUserSelected?.tiktokGroupName || '',
        description: valueUserSelected?.description || '',
        ownerId: ownerId,
        assistant: listAssistant,
        avatar: valueUserSelected?.avatar
          ? {
              fileKey: valueUserSelected?.avatar,
              fileUrl: `${SERVER_FILE_ENDPOINT}/${valueUserSelected.avatar}`,
            }
          : null,
      });
    }
  }, [form, valueUserSelected]);

  // Trưởng team: chỉ MOD hoặc Agency (S3 /studios/manager-candidates); người đang đứng tên nhóm khác hiện mờ.
  const filterOwnerOptions = useCallback(
    async (search: string) => {
      const currentOwnerId = valueUserSelected?.owner?.id;
      try {
        const list = await getManagerCandidates(search);
        return (Array.isArray(list) ? list : []).map((c) => {
          const busy = !c.selectable && c.id !== currentOwnerId;
          const name = c.username || c.nickName || `#${c.id}`;
          return {
            key: String(c.id),
            value: c.id,
            label: busy ? `${name} · đang quản lý ${c.managingStudio?.title || 'nhóm khác'}` : name,
            disabled: busy,
          };
        });
      } catch {
        return [];
      }
    },
    [valueUserSelected],
  );

  const filterOptions = useCallback(async (search: string) => {
    const response: any = await AxiosUtils.get(`${API.searchUser}?query=${search}`);
    return response.data.map((item: any) => ({
      id: item.id,
      label: item.username,
      value: item.id,
    }));
  }, []);

  return (
    <>
      <Modal
        forceRender
        open={isOpen}
        title={valueUserSelected?.id ? 'Sửa nhóm' : 'Thêm nhóm'}
        onCancel={handleCancelCreateEditModal}
        width={800}
        getContainer={false}
        footer={false}
      >
        <Form
          name="form"
          form={form}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          onFinish={onFinish}
          autoComplete="off"
        >
          <label className={styles.labelInput} htmlFor="title">
            Tên Agency / nhóm quản lý
          </label>
          <Form.Item
            name="title"
            rules={[
              REQUIRED,
              {
                max: 200,
                message: 'Không được quá 200 ký tự',
              },
            ]}
            wrapperCol={{ span: 24 }}
          >
            <Input placeholder="Nhập tên nhóm" size="large" />
          </Form.Item>

          <label className={styles.labelInput} htmlFor="tiktokGroupName">
            Tên nhóm trong file TikTok
          </label>
          <Form.Item
            name="tiktokGroupName"
            extra="Nhập nguyên tên cột Nhóm, ví dụ: HT Agency | 12/2021. Dùng để ghép Creator và tự tính tổng KC."
            rules={[{ max: 120, message: 'Tối đa 120 ký tự' }]}
            wrapperCol={{ span: 24 }}
          >
            <AutoComplete
              options={tiktokNames.map((name) => ({ value: name }))}
              filterOption={(input, option) =>
                String(option?.value || '').toLowerCase().includes(input.toLowerCase())
              }
              placeholder="HT Agency | 12/2021"
              size="large"
            />
          </Form.Item>

          <label className={styles.labelInput} htmlFor="description">
            Mô tả nhóm
          </label>
          <Form.Item
            name="description"
            rules={[
              {
                max: 300,
                message: 'Không được quá 300 ký tự',
              },
            ]}
            wrapperCol={{ span: 24 }}
          >
            <Input placeholder="Nhập mô tả nhóm" size="large" />
          </Form.Item>

          <label className={styles.labelInput} htmlFor="ownerId">
            Tài khoản trưởng team (không bắt buộc)
          </label>

          <Form.Item
            name="ownerId"
            wrapperCol={{ span: 24 }}
            extra="Có thể bỏ trống và bổ sung sau bằng nút Sửa. Chưa có trưởng team thì nhóm tạm ẩn thông tin người quản lý."
          >
            <DebounceSelect
              placeholder="Tìm tài khoản MOD hoặc Agency"
              fetchOptions={filterOwnerOptions}
              // Đã có trưởng team thì chỉ đổi người, chưa hỗ trợ gỡ về trống (xoá ô sẽ không có tác dụng).
              allowClear={!valueUserSelected?.owner?.id}
            />
          </Form.Item>

          <label className={styles.labelInput} htmlFor="assistant">
            Trợ lý nhóm
          </label>

          <Form.Item name="assistant" wrapperCol={{ span: 24 }}>
            <DebounceSelect
              mode={'multiple'}
              placeholder="Chọn trợ lý"
              fetchOptions={filterOptions}
            />
          </Form.Item>

          <label className={styles.labelInput} htmlFor="avatar">
            Ảnh
          </label>
          <Form.Item name="avatar" wrapperCol={{ span: 24 }}>
            <CustomizedUpload type={UPLOAD_TYPE.IMAGE} maxFileSize={5} />
          </Form.Item>

          <div className="d-flex justify-center">
            <Space size="large">
              <CustomButton
                bgColor="secondary-green"
                text="Lưu"
                htmlType="submit"
                loading={loading}
              />
              <CustomButton
                text="Hủy"
                bgColor="primary-grey"
                htmlType="button"
                onClick={handleCancelCreateEditModal}
              />
            </Space>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default ModalCreateEdit;
